import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { ArrowLeft, Settings } from 'lucide-react';
import * as icons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { notebookThemes } from '@/themes/notebookThemes';
import { getContainerBackground } from '@/themes/backgroundStyles';
import { useDiarySettings } from '@/hooks/useDiarySettings';
import { useChatSession } from '@/hooks/useChatSession';
import { useVoiceChat } from '@/hooks/useVoiceChat';
import { MessageList } from './MessageList';
import { InputPanel } from './InputPanel';
import { NotebookSettings } from './NotebookSettings';
export function ChatWindow({ character, onBack }) {
    const [input, setInput] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [agenticMode, setAgenticMode] = useState(true);
    const { settings, setSettings, resetSettings } = useDiarySettings();
    const { messages, isLoading, sendMessage, addVoiceMessage, updateVoiceMessage, setMessages, } = useChatSession(character.name, character.greeting);
    const handleUserTranscript = (text, isFinal) => {
        if (agenticMode && isFinal && text.trim()) {
            const userMessage = {
                id: `voice-user-${Date.now()}`,
                content: text,
                sender: 'user',
                timestamp: new Date(),
            };
            addVoiceMessage(userMessage);
        }
        else if (!agenticMode) {
            setInput(prev => prev ? `${prev} ${text}` : text);
        }
    };
    const handleBotTranscript = (text, messageId) => {
        if (!messageId) {
            const newMessageId = `voice-bot-${Date.now()}`;
            addVoiceMessage({
                id: newMessageId,
                content: text,
                sender: 'character',
                timestamp: new Date(),
            });
        }
        else {
            updateVoiceMessage(messageId, text);
        }
    };
    const { isListening, isVoiceConnected, connectVoice, disconnectVoice, toggleBrowserSpeechRecognition, } = useVoiceChat({
        onUserTranscript: handleUserTranscript,
        onBotTranscript: handleBotTranscript,
        agenticMode,
    });
    const handleMicClick = async () => {
        if (agenticMode) {
            if (isVoiceConnected) {
                await disconnectVoice();
            }
            else {
                await connectVoice();
            }
        }
        else {
            toggleBrowserSpeechRecognition();
        }
    };
    const handleSend = async () => {
        if (!input.trim() || isLoading)
            return;
        const messageText = input.trim();
        setInput('');
        await sendMessage(messageText, agenticMode);
    };
    const IconComponent = icons[character.icon];
    const theme = notebookThemes[character.color] || notebookThemes.lavender;
    const customTextStyle = {
        fontFamily: settings.fontFamily,
        fontSize: `${settings.fontSize}px`,
        fontStyle: settings.fontStyle,
        fontWeight: settings.fontWeight,
    };
    const getPageBackground = () => {
        if (settings.backgroundColor === 'default') {
            return theme.pageGradient;
        }
        const opacity = settings.backgroundOpacity / 100;
        return `linear-gradient(to bottom, ${settings.backgroundColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}, ${settings.backgroundColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')})`;
    };
    return (_jsxs("div", { className: "min-h-[100dvh] flex flex-col animate-scale-in", style: getContainerBackground(settings.backgroundStyle, character.color), children: [_jsx("div", { className: "p-4", children: _jsx(Button, { variant: "ghost", size: "icon", onClick: onBack, className: cn("rounded-full", theme.iconBg), children: _jsx(ArrowLeft, { className: "w-5 h-5" }) }) }), _jsx(NotebookSettings, { isOpen: showSettings, onClose: () => setShowSettings(false), settings: settings, onSettingsChange: setSettings, onReset: resetSettings, theme: theme }), _jsx("div", { className: "flex-1 flex items-center justify-center p-4 pb-8", children: _jsx("div", { className: "notebook-container w-full max-w-5xl h-[calc(100dvh-120px)] perspective-1000", children: _jsxs("div", { className: "book-wrapper relative w-full h-full flex rounded-lg shadow-2xl overflow-hidden", children: [_jsx(MessageList, { messages: messages, theme: theme, characterName: character.name, characterPurpose: character.purpose, characterIcon: IconComponent, customTextStyle: customTextStyle, pageBackground: getPageBackground() }), _jsx("div", { className: "book-spine relative w-6 flex-shrink-0", style: {
                                    background: theme.spineGradient,
                                    boxShadow: 'inset 0 0 15px rgba(0,0,0,0.3)'
                                }, children: _jsx("div", { className: "absolute inset-0", style: {
                                        background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 50%, transparent 100%)'
                                    } }) }), _jsxs("div", { className: "relative flex-1", children: [_jsxs("div", { style: {
                                            position: 'absolute',
                                            top: '16px',
                                            right: '70px',
                                            zIndex: 50,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }, children: [_jsx("span", { style: { fontSize: '12px', color: '#6b7280', fontWeight: 500 }, children: "Agentic" }), _jsx("button", { onClick: () => setAgenticMode(!agenticMode), style: {
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    backgroundColor: agenticMode ? '#7c3aed' : '#d1d5db',
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    border: 'none',
                                                    transition: 'background-color 0.2s',
                                                }, children: _jsx("div", { style: {
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: agenticMode ? '22px' : '2px',
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                        transition: 'left 0.2s',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                    } }) })] }), _jsx("button", { onClick: () => setShowSettings(!showSettings), style: {
                                            position: 'absolute',
                                            top: '16px',
                                            right: '16px',
                                            zIndex: 50,
                                            width: '44px',
                                            height: '44px',
                                            borderRadius: '50%',
                                            backgroundColor: '#1f2937',
                                            color: 'white',
                                            border: '2px solid #374151',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }, children: _jsx(Settings, { style: { width: '22px', height: '22px' } }) }), _jsx(InputPanel, { input: input, setInput: setInput, isLoading: isLoading, isListening: isListening, isVoiceConnected: isVoiceConnected, onSend: handleSend, onMicClick: handleMicClick, theme: theme, customTextStyle: customTextStyle, pageBackground: getPageBackground() })] })] }) }) })] }));
}
